import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { calendarApi, type RecordingRow } from '@/api/calendar';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Modal } from '@/components/ui/Modal';

// 🔴 ЗАПИСЬ УРОКОВ НЕ РАБОТАЕТ. Адрес ведёт на дроплет `64.227.74.212`, которого
// давно нет, а на новом сервере голоса запись (egress) не настроена вовсе —
// это отдельный контейнер и своё хранилище. Записей в базе ноль, поэтому список
// всегда пуст и до проигрывания дело не доходит.
//
// ⚠️ НЕ ПРИНИМАТЬ ЭТОТ АДРЕС ЗА РАБОЧИЙ. Заведена задача 145: поднять egress и
// складывать записи в наш MinIO, а адрес получать с сервера, а не зашивать сюда.
const REC_BASE = 'https://live-self.missdana.kz/rec';

function fmtDur(sec?: number): string {
  if (!sec || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function AdminRecordingsPage() {
  const { t } = useTranslation();
  const [active, setActive] = useState<RecordingRow | null>(null);

  const recordings = useQuery({
    queryKey: ['admin', 'recordings'],
    queryFn: () => calendarApi.listRecordings(200, 0),
  });

  const items = recordings.data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t('adminRecordings.title')}
        subtitle={recordings.isLoading ? t('common.loading') : t('adminRecordings.count', { count: items.length })}
      />

      {recordings.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : !items.length ? (
        <Card variant="subtle" className="py-10 text-center text-on-surface-variant">
          <span className="material-symbols-outlined mb-2 text-4xl">videocam_off</span>
          <p className="font-semibold text-on-surface">{t('adminRecordings.emptyTitle')}</p>
          <p className="text-sm">{t('adminRecordings.emptyDesc')}</p>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b border-outline-variant/40 text-xs uppercase tracking-wide text-on-surface-variant">
              <tr>
                <th className="px-4 py-3">{t('adminRecordings.colTitle')}</th>
                <th className="px-4 py-3">{t('adminRecordings.colGroup')}</th>
                <th className="px-4 py-3">{t('adminRecordings.colTeacher')}</th>
                <th className="px-4 py-3">{t('adminRecordings.colStarted')}</th>
                <th className="px-4 py-3">{t('adminRecordings.colDuration')}</th>
                <th className="px-4 py-3">{t('adminRecordings.colPeople')}</th>
                <th className="px-4 py-3">{t('adminRecordings.colSize')}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-outline-variant/20 last:border-0 hover:bg-surface-container-low">
                  <td className="px-4 py-3 font-medium text-on-surface">{r.title || '—'}</td>
                  <td className="px-4 py-3">{r.group || '—'}</td>
                  <td className="px-4 py-3 text-on-surface-variant">{r.created_by_name || '—'}</td>
                  <td className="px-4 py-3 tabular-nums text-on-surface-variant">{fmtDate(r.recording_started_at)}</td>
                  <td className="px-4 py-3 tabular-nums">{fmtDur(r.duration_sec)}</td>
                  <td className="px-4 py-3 tabular-nums">{r.participants_count ?? 0}</td>
                  <td className="px-4 py-3 tabular-nums text-on-surface-variant">
                    {r.size_mb ? `${r.size_mb.toFixed(1)} МБ` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {r.status === 'recording' ? (
                      <Pill variant="danger">{t('adminRecordings.recording')}</Pill>
                    ) : (
                      <button
                        onClick={() => setActive(r)}
                        className="inline-flex items-center gap-1 rounded-pill bg-primary px-3 py-1.5 text-xs font-semibold text-on-primary transition hover:opacity-90"
                      >
                        <span className="material-symbols-outlined text-[16px]">play_arrow</span>
                        {t('adminRecordings.watch')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={active !== null} onClose={() => setActive(null)} title={active?.title || t('adminRecordings.title')} size="xl">
        {active && (
          <div className="space-y-4">
                        <video src={`${REC_BASE}/${active.file_name}`} controls className="max-h-[60vh] w-full rounded-card bg-black" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Meta label={t('adminRecordings.colGroup')} value={active.group || '—'} />
              <Meta label={t('adminRecordings.colTeacher')} value={active.created_by_name || '—'} />
              <Meta label={t('adminRecordings.scheduledStart')} value={fmtDate(active.scheduled_start)} />
              <Meta label={t('adminRecordings.colStarted')} value={fmtDate(active.recording_started_at)} />
              <Meta label={t('adminRecordings.colDuration')} value={fmtDur(active.duration_sec)} />
              <Meta label={t('adminRecordings.colSize')} value={active.size_mb ? `${active.size_mb.toFixed(1)} МБ` : '—'} />
            </div>
            {active.attendees && active.attendees.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                  {t('adminRecordings.attendees')} ({active.attendees.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {active.attendees.map((a) => (
                    <Pill key={a.identity} variant="neutral">{a.name || a.identity.slice(0, 8)}</Pill>
                  ))}
                </div>
              </div>
            )}
            {active.checked_in && active.checked_in.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
                  {t('adminRecordings.checkedIn')} ({active.checked_in.length})
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {active.checked_in.map((a) => (
                    <Pill key={a.identity} variant="success">{a.name || a.identity.slice(0, 8)}</Pill>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card bg-surface-container-low px-3 py-2">
      <p className="text-xs text-on-surface-variant">{label}</p>
      <p className="font-semibold text-on-surface">{value}</p>
    </div>
  );
}
